// MySQL identifier helpers: backtick / double-quote round-trip.
//
// MySQL identifier rules:
//   * Bare identifiers match `^[A-Za-z0-9_$][A-Za-z0-9_$]*$` — MySQL allows
//     leading digits and `$`, but only the conservative `^[A-Za-z_][...]`
//     subset is treated as bare here so identifiers that *might* be parsed as
//     numeric literals get backtick-quoted defensively.
//   * `` `name` `` is the always-safe quoted form (works in every sql_mode).
//     A literal backtick inside the body is escaped by doubling (`` `` ``).
//   * `"name"` is also a valid identifier delimiter when `@@sql_mode` contains
//     `ANSI_QUOTES`. Otherwise `"name"` is a string literal. The resolver
//     defaults to backtick output because it is the canonical / always-safe
//     form. `unquoteIdentifier` recognizes both quote forms because user-typed
//     SQL may legitimately use either.
//   * Identifier comparison is case-insensitive when `@@lower_case_table_names`
//     is `1` or `2` (the default on macOS/Windows; `1` on most Linux MySQL
//     installs). Set `0` keeps comparisons case-sensitive (Linux file-system
//     dependent). The resolver does not control which mode is in effect — it
//     reads the session variable from the schema cache. The `foldIdentifier`
//     helper here always lower-cases unquoted identifiers because MySQL's
//     parser is case-insensitive for keywords / built-in functions regardless
//     of `lower_case_table_names`; the per-table lookup performs the strict
//     comparison when needed.
//
// Pure module — no Monaco runtime, no I/O. ANSI_QUOTES handling is a runtime
// option passed to `unquoteIdentifier`; the resolver wires it from the cached
// session-variable value.

const BARE_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export type MysqlQuotingOptions = {
  /** True when `@@sql_mode` contains `ANSI_QUOTES`. */
  readonly ansiQuotes?: boolean;
};

export function needsBacktickQuote(identifier: string): boolean {
  if (identifier === '') return true;
  return !BARE_IDENT_RE.test(identifier);
}

export function backtickQuote(identifier: string): string {
  return `\`${identifier.replace(/`/g, '``')}\``;
}

/** Round-trip-safe MySQL identifier — backtick-quoted only when required. */
export function formatIdentifier(identifier: string): string {
  return needsBacktickQuote(identifier) ? backtickQuote(identifier) : identifier;
}

/** Compose `db.name` with per-part backtick-on-demand. */
export function formatQualifiedTwoPart(database: string, name: string): string {
  return `${formatIdentifier(database)}.${formatIdentifier(name)}`;
}

/**
 * Strip the surrounding backticks (or `"…"` when `ansiQuotes` is true) from a
 * single identifier token, applying the doubled-escape rule. Returns the
 * input verbatim when not quoted — caller is responsible for case-folding
 * bare identifiers if a case-insensitive comparison is required (use
 * `eqIdentifier` / `foldIdentifier`).
 */
export function unquoteIdentifier(
  token: string,
  options: MysqlQuotingOptions = {},
): string {
  if (token.length < 2) return token;
  const first = token[0]!;
  const last = token[token.length - 1]!;
  if (first === '`' && last === '`') {
    return token.slice(1, -1).replace(/``/g, '`');
  }
  if (options.ansiQuotes && first === '"' && last === '"') {
    return token.slice(1, -1).replace(/""/g, '"');
  }
  return token;
}

/**
 * MySQL identifier folding for case-comparison purposes.
 *
 * Backtick-quoted (`` `Foo` ``) — preserves case (verbatim after unquoting).
 * Double-quoted under ANSI_QUOTES — preserves case.
 * Unquoted — folds to lower-case.
 *
 * The lower-cased form matches MySQL's case-insensitive identifier behaviour
 * with `lower_case_table_names ∈ {1, 2}`. With `lower_case_table_names = 0`
 * on Linux the OS file system enforces case-sensitive table file names — but
 * column names, alias names, and keyword matches stay case-insensitive even
 * in that mode, so a global lower-case fold is safe for completion lookups.
 */
export function foldIdentifier(
  token: string,
  options: MysqlQuotingOptions = {},
): string {
  if (token.length >= 2) {
    const first = token[0]!;
    const last = token[token.length - 1]!;
    if (first === '`' && last === '`') {
      return token.slice(1, -1).replace(/``/g, '`');
    }
    if (options.ansiQuotes && first === '"' && last === '"') {
      return token.slice(1, -1).replace(/""/g, '"');
    }
  }
  return token.toLowerCase();
}

/**
 * MySQL semantic equality on two identifier tokens. Quoted identifiers
 * preserve case; unquoted identifiers fold to lower case. `Customer` ==
 * `customer` (both unquoted), but `Customer` !== `` `Customer` `` (the
 * backtick form preserves case verbatim).
 */
export function eqIdentifier(
  a: string,
  b: string,
  options: MysqlQuotingOptions = {},
): boolean {
  return foldIdentifier(a, options) === foldIdentifier(b, options);
}
